import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  selectUser,
  selectIsProvider,
} from "../../store/selectors/authSelectors";
import { useTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import { Layout } from "../../components/layout";
import {
  GiftIcon,
  MaleIcon,
  PhoneIcon,
  EmailIcon,
  RightArrowIcon,
  SadFaceIcon,
  HappyFaceIcon,
  SortIcon,
  SortDownIcon,
} from "../../components/Icons";
import {
  useModal,
  StartSessionModalContent,
  SessionData,
  RecordingSessionModal,
} from "../../components/Modal";
import { performMicrophoneCheck } from "../../utils/microphoneCheck";
import {
  PageContainer,
  PageHeader,
  StartSessionButton,
  PatientDetailsCard,
  PatientInfoSection,
  PatientNameBlock,
  PatientDetailsLabel,
  PatientName,
  PatientDetailColumn,
  PatientDetailItem,
  PatientDetailText,
  AllergiesSection,
  AllergiesLabel,
  AllergiesLabelText,
  AllergiesText,
  NoAllergiesText,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SessionsTable,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  ProviderCell,
  ProviderAvatar,
  ProviderName,
  ActionLink,
  NoDataText,
} from "./PatientProfilePage.styled";
import {
  patientsService,
  PatientDetails,
} from "../../services/patientsService";
import api from "../../api/axios";

const PatientProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const currentUser = useSelector(selectUser);
  const isProvider = useSelector(selectIsProvider);
  const { openModal, closeModal } = useModal();

  const [patient, setPatient] = useState<PatientDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchInitiatedRef = useRef(false);

  const [isCheckingMic, setIsCheckingMic] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [micCheckError, setMicCheckError] = useState<{
    type:
      | "NO_DEVICE"
      | "PERMISSION_DENIED"
      | "SYSTEM_MUTED"
      | "BROWSER_NOT_SUPPORTED"
      | "UNKNOWN";
    message: string;
    title: string;
  } | null>(null);

  // Sorting state
  type SortField = "provider" | "date";
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Fetch patient data
  useEffect(() => {
    if (!id || fetchInitiatedRef.current) return;

    fetchInitiatedRef.current = true;
    setLoading(true);

    const patientId = Number(id);

    patientsService
      .getPatientById(patientId)
      .then((data) => {
        setPatient(data);
        api.post(`/patients/${patientId}/audit/profile-viewed`).catch((err) => {
          console.error("Error sending profile view audit:", err);
        });
      })
      .catch((err) => {
        console.error("Error fetching patient:", err);
        setError(t("patientProfile.loadError"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, t]);

  // Format phone number to +1 (XXX) XXX-XXXX
  const formatPhone = (phone: string): string => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    const localDigits =
      digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (localDigits.length === 10) {
      return `+1 (${localDigits.slice(0, 3)}) ${localDigits.slice(
        3,
        6,
      )}-${localDigits.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (isoDate: string): string => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleSessionClick = (sessionId: number) => {
    navigate(`/sessions/${sessionId}`);
  };

  const handleTreatmentPlanClick = (treatmentPlanId: number | string) => {
    navigate(`/treatment-plan/${treatmentPlanId}`);
  };

  const handleViewAllSessions = () => {
    if (!patient) return;
    // Navigate to all sessions with patient filter applied
    navigate(`/sessions?patientId=${patient.id}`);
  };

  const getErrorTitle = (errorType: string): string => {
    switch (errorType) {
      case "NO_DEVICE":
        return "Microphone Not Found";
      case "PERMISSION_DENIED":
        return "Microphone Access Denied";
      case "SYSTEM_MUTED":
        return retryCount === 0
          ? "No Audio Input Detected"
          : "Microphone Not Found";
      case "BROWSER_NOT_SUPPORTED":
        return "Browser Not Supported";
      default:
        return "Microphone Error";
    }
  };

  const handleProceedSession = useCallback(
    (sessionData: SessionData) => {
      closeModal("start-session-modal");

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          openModal({
            id: "recording-session-modal",
            title: "",
            content: (
              <RecordingSessionModal
                sessionId={sessionData.sessionId}
                sessionType={sessionData.sessionType}
                patientId={sessionData.patientId}
              />
            ),
            showCloseButton: false,
            closeOnBackdropClick: false,
            maxWidth: 660,
            width: "660px",
            fullWidth: false,
            containerPadding: "40px",
            contentPadding: "0",
          });
        });
      });
    },
    [closeModal, openModal],
  );

  const handleStartSession = useCallback(async () => {
    if (!patient) return;

    setIsCheckingMic(true);
    setMicCheckError(null);

    try {
      const result = await performMicrophoneCheck();

      if (!result.success) {
        const errorType = result.errorType || "UNKNOWN";

        if (errorType === "SYSTEM_MUTED") {
          setRetryCount((prev) => prev + 1);
        }

        setMicCheckError({
          type: errorType,
          title: getErrorTitle(errorType),
          message:
            result.error ||
            "An unexpected error occurred while checking microphone. Please try again.",
        });
        return;
      }

      setRetryCount(0);

      openModal({
        id: "start-session-modal",
        title: t("startSessionModal.title"),
        content: (
          <StartSessionModalContent
            onProceed={handleProceedSession}
            initialPatient={{
              id: patient.id,
              label: patient.basicInfo.fullName,
            }}
            lockPatient
          />
        ),
        maxWidth: "sm",
        fullWidth: true,
        containerPadding: "40px",
        contentPadding: "0",
      });
    } catch (err) {
      console.error("Error during microphone check:", err);
      setMicCheckError({
        type: "UNKNOWN",
        title: "Microphone Error",
        message:
          "An unexpected error occurred while checking microphone. Please try again.",
      });
    } finally {
      setIsCheckingMic(false);
    }
    // getErrorTitle uses retryCount — intentional for title copy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, openModal, handleProceedSession, t, retryCount]);

  const handleRetryMicrophone = () => {
    setMicCheckError(null);
    void handleStartSession();
  };

  const handleCancelMicrophone = () => {
    setMicCheckError(null);
    setRetryCount(0);
  };

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort sessions
  const sortedSessions = useMemo(() => {
    if (!patient) return [];

    return [...patient.sessions].sort((a, b) => {
      let comparison = 0;

      if (sortField === "provider") {
        comparison = a.providerName.localeCompare(b.providerName);
      } else if (sortField === "date") {
        comparison =
          new Date(a.sessionDate).getTime() - new Date(b.sessionDate).getTime();
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [patient, sortField, sortDirection]);

  // Render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <SortIcon style={{ opacity: 0.5 }} />;
    }
    return sortDirection === "asc" ? (
      <SortDownIcon style={{ transform: "rotate(180deg)" }} />
    ) : (
      <SortDownIcon />
    );
  };

  if (loading) {
    return (
      <Layout>
        <PageContainer>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        </PageContainer>
      </Layout>
    );
  }

  if (error || !patient) {
    return (
      <Layout>
        <PageContainer>
          <NoDataText>{error || t("patientProfile.notFound")}</NoDataText>
        </PageContainer>
      </Layout>
    );
  }

  const hasAllergies = patient.allergies.length > 0;

  return (
    <Layout>
      <PageContainer>
        <PageHeader>
          <Typography variant="h1">{t("patientProfile.title")}</Typography>
          {isProvider && (
            <StartSessionButton
              variant="contained"
              endIcon={<RightArrowIcon />}
              onClick={() => void handleStartSession()}
              disabled={isCheckingMic}
            >
              {isCheckingMic
                ? t("dashboard.checkingMicrophone")
                : t("dashboard.startSession")}
            </StartSessionButton>
          )}
        </PageHeader>

        {/* Patient Details Card */}
        <PatientDetailsCard>
          <PatientInfoSection>
            <PatientNameBlock>
              <PatientDetailsLabel>
                {t("patientProfile.patientDetails")}:
              </PatientDetailsLabel>
              <PatientName>{patient.basicInfo.fullName}</PatientName>
            </PatientNameBlock>

            <PatientDetailColumn>
              <PatientDetailItem>
                <GiftIcon />
                <PatientDetailText>
                  {formatDate(patient.basicInfo.dateOfBirth)}
                </PatientDetailText>
              </PatientDetailItem>
              <PatientDetailItem>
                <MaleIcon />
                <PatientDetailText>
                  {patient.basicInfo.gender}
                </PatientDetailText>
              </PatientDetailItem>
            </PatientDetailColumn>

            <PatientDetailColumn>
              <PatientDetailItem>
                <PhoneIcon />
                <PatientDetailText>
                  {formatPhone(patient.basicInfo.phone)}
                </PatientDetailText>
              </PatientDetailItem>
              <PatientDetailItem>
                <EmailIcon />
                <PatientDetailText>{patient.basicInfo.email}</PatientDetailText>
              </PatientDetailItem>
            </PatientDetailColumn>
          </PatientInfoSection>

          <AllergiesSection hasAllergies={hasAllergies}>
            <AllergiesLabel>
              {hasAllergies ? (
                <SadFaceIcon fill="#EF5350" width={16} height={16} />
              ) : (
                <HappyFaceIcon fill="#296C50" width={16} height={16} />
              )}
              <AllergiesLabelText hasAllergies={hasAllergies}>
                {t("patientProfile.allergies")}:
              </AllergiesLabelText>
            </AllergiesLabel>
            {hasAllergies ? (
              <AllergiesText>{patient.allergies.join(", ")}</AllergiesText>
            ) : (
              <NoAllergiesText>
                {t("patientProfile.noAllergies")}
              </NoAllergiesText>
            )}
          </AllergiesSection>
        </PatientDetailsCard>

        {/* Sessions Section */}
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>
              {t("patientProfile.sessions")} ({patient.sessions.length})
            </SectionTitle>
            <Button
              variant="outlined"
              onClick={handleViewAllSessions}
              endIcon={<RightArrowIcon fill="currentColor" />}
            >
              {t("patientProfile.viewAllSessions")}
            </Button>
          </SectionHeader>

          <SessionsTable>
            <TableHeader>
              <TableHeaderCell
                onClick={() => handleSort("provider")}
                style={{ cursor: "pointer" }}
              >
                {t("patientProfile.provider")}
                {renderSortIcon("provider")}
              </TableHeaderCell>
              <TableHeaderCell
                onClick={() => handleSort("date")}
                style={{ cursor: "pointer" }}
              >
                {t("patientProfile.date")}
                {renderSortIcon("date")}
              </TableHeaderCell>
              <TableHeaderCell>{t("patientProfile.actions")}</TableHeaderCell>
            </TableHeader>

            {sortedSessions.length > 0 ? (
              sortedSessions.map((session) => (
                <TableRow key={session.sessionId}>
                  <TableCell>
                    <ProviderCell>
                      <ProviderAvatar>
                        {session.providerAvatar ? (
                          <img
                            src={session.providerAvatar}
                            alt={session.providerName}
                          />
                        ) : (
                          session.providerName.charAt(0)
                        )}
                      </ProviderAvatar>
                      <ProviderName
                        onClick={() => {
                          const providerId =
                            session.providerId ?? patient.basicInfo.providerId;
                          if (providerId === currentUser?.id) {
                            navigate("/profile");
                          } else {
                            navigate(`/user-management/${providerId}`);
                          }
                        }}
                      >
                        {session.providerName}
                      </ProviderName>
                    </ProviderCell>
                  </TableCell>
                  <TableCell>{formatDate(session.sessionDate)}</TableCell>
                  <TableCell>
                    <ActionLink
                      onClick={() => handleSessionClick(session.sessionId)}
                    >
                      {t("patientProfile.sessionDetails")}
                    </ActionLink>
                    {session.treatmentPlanId != null && (
                      <ActionLink
                        onClick={() =>
                          handleTreatmentPlanClick(session.treatmentPlanId!)
                        }
                      >
                        {t("patientProfile.treatmentPlan")}
                        <RightArrowIcon fill="#3A8AB9" width={14} height={14} />
                      </ActionLink>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <NoDataText>{t("patientProfile.noSessions")}</NoDataText>
            )}
          </SessionsTable>
        </SectionContainer>
      </PageContainer>

      <Snackbar
        open={!!micCheckError}
        autoHideDuration={null}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          sx={{ width: "100%", alignItems: "flex-start" }}
          action={
            <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
              <Button color="inherit" size="small" onClick={handleRetryMicrophone}>
                Retry
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={handleCancelMicrophone}
              >
                Cancel
              </Button>
            </Box>
          }
        >
          <Typography fontWeight={700}>{micCheckError?.title}</Typography>
          {micCheckError?.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default PatientProfilePage;
